import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader } from "@/components/Loader";
import PptViewer from "@/components/nurse/PptViewer";
import PdfViewer from "@/components/nurse/PdfViewer";
import YouTubePlayer from "@/components/nurse/YouTubePlayer";
import { fileUrl } from "@/lib/api";
import { ChevronLeft, FileText, Video, ClipboardCheck, CheckCircle2, Lock } from "lucide-react";

function StepHeader({ n, title, done, active }) {
  return (
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
        done ? "bg-[#004e33] text-white" : active ? "bg-[#003d9b] text-white" : "bg-gray-200 text-gray-500"
      }`}>
        {done ? <CheckCircle2 className="w-5 h-5" /> : n}
      </div>
      <h2 className="font-semibold text-[#1c1b1b]">{title}</h2>
    </div>
  );
}

export default function ModuleView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [ppt, setPpt] = useState(false);
  const [video, setVideo] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/nurse/modules/${id}`);
      setData(r.data);
      setPpt(r.data.assignment.ppt_completed);
      setVideo(r.data.assignment.video_completed);
    } catch (e) { toast.error(apiError(e)); navigate("/app"); }
  };
  useEffect(() => { load(); }, [id]);

  const completePpt = async () => {
    if (ppt) return;
    try { await api.post(`/nurse/modules/${id}/complete-ppt`); setPpt(true); toast.success("Presentation completed"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const onVideoProgress = (secs, completed) => {
    api.post(`/nurse/modules/${id}/video-progress`, { watch_seconds: secs, completed }).catch(() => {});
  };
  const onVideoEnded = () => { setVideo(true); toast.success("Video completed"); };

  if (!data) return <Loader />;
  const m = data.module;
  const assessmentUnlocked = ppt && video;

  return (
    <div className="max-w-md mx-auto pb-10" data-testid="module-view">
      <button onClick={() => navigate("/app")} data-testid="module-back-btn" className="flex items-center gap-1 text-sm font-semibold text-[#003d9b] mb-4 h-11">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>
      <h1 className="text-[24px] font-bold text-[#1c1b1b] leading-tight">{m.title}</h1>
      <p className="text-sm text-[#434654] mt-1 mb-6">{m.description}</p>

      <p className="text-xs uppercase tracking-wider text-[#434654] font-bold mb-4">Learning Path</p>

      <section className="mb-8" data-testid="step-presentation">
        <StepHeader n={1} title="Presentation" done={ppt} active={!ppt} />
        {m.pdf_path ? (
          <PdfViewer pdfUrl={fileUrl(m.pdf_path)} completed={ppt} onComplete={completePpt} />
        ) : m.slide_paths?.length ? (
          <PptViewer slidePaths={m.slide_paths} completed={ppt} onComplete={completePpt} />
        ) : (
          <div className="text-sm text-[#434654] p-4 border border-[#E5E7EB] rounded-lg bg-white" data-testid="presentation-empty">
            No presentation uploaded yet.
          </div>
        )}
        <p className="text-xs text-[#434654] mt-2">Reach the last slide to complete this step.</p>
      </section>

      <section className="mb-8" data-testid="step-video">
        <StepHeader n={2} title="Video" done={video} active={ppt && !video} />
        <YouTubePlayer url={m.youtube_url} completed={video} onProgress={onVideoProgress} onEnded={onVideoEnded} />
        <p className="text-xs text-[#434654] mt-2">Watch to the end to complete this step.</p>
      </section>

      <section data-testid="step-assessment">
        <StepHeader n={3} title="Assessment" done={data.latest_result_passed === true} active={assessmentUnlocked} />
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-5">
          {!assessmentUnlocked ? (
            <div className="flex items-center gap-2 text-[#434654] text-sm" data-testid="assessment-locked">
              <Lock className="w-4 h-4" /> Complete the presentation and video first.
            </div>
          ) : !data.assessment_available ? (
            <p className="text-sm text-[#434654]">No assessment published for this module yet.</p>
          ) : data.latest_result_passed === true ? (
            <div className="space-y-3">
              <p className="text-sm text-[#004e33] font-semibold flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Passed
              </p>
              <button onClick={() => navigate(`/app/results/${data.latest_result_id}`)} data-testid="view-result-btn"
                className="w-full h-11 rounded-lg border border-[#E5E7EB] font-semibold text-[#003d9b] hover:bg-gray-50 transition-colors">
                Review Answers
              </button>
            </div>
          ) : data.latest_result_status === "submitted" ? (
            <p className="text-sm text-[#F59E0B] font-semibold" data-testid="assessment-awaiting">
              Submitted — awaiting MITRA grading.
            </p>
          ) : (
            <div className="space-y-3">
              <div className="flex gap-6">
                <div><p className="text-2xl font-bold text-[#003d9b]">{data.question_count}</p><p className="text-xs text-[#434654]">Questions</p></div>
                <div><p className="text-2xl font-bold text-[#003d9b]">{data.pass_threshold}%</p><p className="text-xs text-[#434654]">Passing</p></div>
              </div>
              {data.latest_result_passed === false && (
                <button onClick={() => navigate(`/app/results/${data.latest_result_id}`)} data-testid="view-failed-result-btn"
                  className="w-full h-11 rounded-lg border border-[#E5E7EB] font-semibold text-[#003d9b] hover:bg-gray-50 transition-colors">
                  View Last Result
                </button>
              )}
              <button onClick={() => navigate(`/app/modules/${id}/assessment`)} data-testid="start-assessment-btn"
                className="w-full h-12 rounded-lg bg-[#003d9b] text-white font-semibold hover:bg-[#0052cc] transition-colors">
                Start Assessment
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
